import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { get } from 'lodash';
import { getAvatarStyle, isNewStyleAvatar } from '../../../../lib/s3';

/**
 * EmptyMessages - Display when conversation has no messages
 * 
 * Features beautiful empty state with encouraging copy
 */
const EmptyMessages = ({ otherParticipant }) => {
  const intl = useIntl();
  const displayName = otherParticipant?.allowed_name || otherParticipant?.name || 'this person';
  const isAdmin = otherParticipant?.is_admin;

  return (
    <>
      {/* CSS Animations */}
      <style>
        {`
          @keyframes float-gentle {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
          }
          
          @keyframes pulse-glow {
            0%, 100% { 
              box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15), 0 0 20px var(--succession-primary-20);
            }
            50% { 
              box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2), 0 0 30px var(--succession-primary-50);
            }
          }
          
          @keyframes sparkle {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `}
      </style>
      
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: '400px',
        padding: '40px',
        textAlign: 'center',
        background: 'var(--succession-bg-secondary)' // Changed from hardcoded gradient
      }}>
      {/* Participant Avatar */}
      <div style={{
        width: '96px',
        height: '96px',
        borderRadius: '50%',
        marginBottom: '32px',
        animation: 'float-gentle 4s ease-in-out infinite, pulse-glow 3s ease-in-out infinite',
        border: '4px solid var(--succession-bg-primary)', // Changed from white
        position: 'relative',
        overflow: 'hidden'
      }}>
        {get(otherParticipant, 'profile_picture') ? (
          <img
            src={getAvatarStyle(get(otherParticipant, 'profile_picture', '')).url}
            alt={displayName}
            style={{
              width: '100%',
              height: isNewStyleAvatar(get(otherParticipant, 'profile_picture', '')) ? '100%' : '100%',
              objectFit: isNewStyleAvatar(get(otherParticipant, 'profile_picture', '')) ? 'contain' : 'cover',
              objectPosition: isNewStyleAvatar(get(otherParticipant, 'profile_picture', '')) ? 'bottom center' : undefined,
              transform: isNewStyleAvatar(get(otherParticipant, 'profile_picture', '')) ? 'scale(0.85)' : undefined,
              transformOrigin: isNewStyleAvatar(get(otherParticipant, 'profile_picture', '')) ? 'bottom center' : undefined,
              borderRadius: '50%'
            }}
            onError={(e) => {
              // Fallback to gradient avatar if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        
        {/* Fallback gradient avatar */}
        <div style={{
          display: get(otherParticipant, 'profile_picture') ? 'none' : 'flex',
          width: '100%',
          height: '100%',
          background: 'var(--succession-primary)', // Changed from complex gradient function
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--succession-bg-primary)', // Changed from white
          fontWeight: '600',
          fontSize: '32px',
          borderRadius: '50%'
        }}>
          {displayName.charAt(0).toUpperCase()}
        </div>
        
        {/* Online status indicator */}
        <div style={{
          position: 'absolute',
          bottom: '6px',
          right: '6px',
          width: '24px',
          height: '24px',
          backgroundColor: 'var(--succession-success)', // Changed from hardcoded blue
          borderRadius: '50%',
          border: '4px solid var(--succession-bg-primary)', // Changed from white
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15), 0 0 20px var(--succession-success-bg)', // Changed from hardcoded blue
          animation: 'sparkle 2s ease-in-out infinite'
        }} />
      </div>

      {/* Welcome Message */}
      <h3 style={{
        fontSize: '28px',
        fontWeight: '700',
        color: 'var(--succession-text-primary)', // Changed from gradient text
        margin: '0 0 16px 0',
        lineHeight: '1.3',
        letterSpacing: '0.025em'
      }}>
        {intl.formatMessage({ id: 'succession.messaging.empty_messages.start_conversation' }, { name: displayName })}
      </h3>

      {/* Subtitle */}
      <p style={{
        fontSize: '16px',
        color: 'var(--succession-text-secondary)', // Changed from #64748b
        lineHeight: '1.6',
        margin: '0 0 32px 0',
        maxWidth: '400px'
      }}>
        {intl.formatMessage({ id: 'succession.messaging.empty_messages.beginning_subtitle' })}{' '}
        {isAdmin ? intl.formatMessage({ id: 'succession.messaging.empty_messages.admin_subtitle' }) : intl.formatMessage({ id: 'succession.messaging.empty_messages.user_subtitle' })}
      </p>

      {/* Conversation Starters */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '100%',
        maxWidth: '400px'
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '500',
          color: 'var(--succession-text-primary)', // Changed from #374151
          marginBottom: '8px'
        }}>
          {intl.formatMessage({ id: 'succession.messaging.empty_messages.conversation_starters_label' })}
        </div>
        
        {getConversationStarters(isAdmin, intl).map((starter, index) => (
          <ConversationStarter key={index} text={starter} />
        ))}
      </div>

      {/* Features */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        marginTop: '40px',
        fontSize: '13px',
        color: 'var(--succession-text-muted)' // Changed from #64748b
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <svg style={{ width: '16px', height: '16px', color: 'var(--succession-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>{intl.formatMessage({ id: 'succession.messaging.empty_messages.real_time_messaging' })}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <svg style={{ width: '16px', height: '16px', color: 'var(--succession-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span>{intl.formatMessage({ id: 'succession.messaging.empty_messages.secure_private' })}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <svg style={{ width: '16px', height: '16px', color: 'var(--succession-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span>{intl.formatMessage({ id: 'succession.messaging.empty_messages.instant_delivery' })}</span>
        </div>
      </div>
    </div>
    </>
  );
};

/**
 * ConversationStarter - Clickable conversation starter
 */
const ConversationStarter = ({ text }) => {
  return (
    <div
      style={{
        padding: '16px 20px',
        background: 'var(--succession-bg-primary)', // Changed from hardcoded gradient
        border: '2px solid transparent',
        borderRadius: '12px',
        fontSize: '15px',
        color: 'var(--succession-text-primary)', // Changed from #374151
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        textAlign: 'left',
        position: 'relative',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
      }}
      onMouseEnter={(e) => {
        e.target.style.background = 'var(--succession-primary)'; // Changed from hardcoded gradient
        e.target.style.color = 'var(--succession-bg-primary)'; // Changed from #ffffff
        e.target.style.transform = 'translateY(-2px) scale(1.02)';
        e.target.style.boxShadow = '0 8px 20px var(--succession-primary-20)'; // Changed from hardcoded blue
        const svg = e.target.querySelector('svg');
        if (svg) svg.style.color = 'var(--succession-bg-primary)'; // Changed from #ffffff
      }}
      onMouseLeave={(e) => {
        e.target.style.background = 'var(--succession-bg-primary)'; // Changed from hardcoded gradient
        e.target.style.color = 'var(--succession-text-primary)'; // Changed from #374151
        e.target.style.transform = 'translateY(0) scale(1)';
        e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
        const svg = e.target.querySelector('svg');
        if (svg) svg.style.color = 'var(--succession-text-muted)'; // Changed from #94a3b8
      }}
      onClick={() => {
        // This would populate the message composer
        console.log('Conversation starter clicked:', text);
      }}
    >
      {text}
      
      {/* Click indicator */}
      <div style={{
        position: 'absolute',
        right: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        opacity: 0.6,
        transition: 'all 0.3s ease'
      }}>
        <svg style={{ width: '16px', height: '16px', color: 'var(--succession-text-muted)', transition: 'color 0.3s ease' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      </div>
    </div>
  );
};

/**
 * Get conversation starters based on user type
 */
const getConversationStarters = (isAdmin, intl) => {
  if (isAdmin) {
    return [
      intl.formatMessage({ id: 'succession.messaging.empty_messages.admin_starter_1' }),
      intl.formatMessage({ id: 'succession.messaging.empty_messages.admin_starter_2' }),
      intl.formatMessage({ id: 'succession.messaging.empty_messages.admin_starter_3' }),
      intl.formatMessage({ id: 'succession.messaging.empty_messages.admin_starter_4' }),
    ];
  }

  return [
    intl.formatMessage({ id: 'succession.messaging.empty_messages.user_starter_1' }),
    intl.formatMessage({ id: 'succession.messaging.empty_messages.user_starter_2' }),
    intl.formatMessage({ id: 'succession.messaging.empty_messages.user_starter_3' }),
    intl.formatMessage({ id: 'succession.messaging.empty_messages.user_starter_4' }),
  ];
};

/**
 * Generate avatar gradient based on name
 */
const getAvatarGradient = (name) => {
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)'
  ];
  
  const charCode = name.charCodeAt(0) || 0;
  return gradients[charCode % gradients.length];
};

EmptyMessages.propTypes = {
  otherParticipant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    allowed_name: PropTypes.string,
    is_admin: PropTypes.bool
  })
};

ConversationStarter.propTypes = {
  text: PropTypes.string.isRequired
};

export default EmptyMessages; 